import { AuthTokenData } from "@vixi/types";
import { Account } from "@vixi/types";
import { Nullable, NullablePartial } from "@vixi/types";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface AuthState extends NullablePartial<AuthTokenData> {
  userInfo: Nullable<Account>;

  registeringUser: Nullable<{
    email: string;
  }>;
}

export interface AuthActions {
  setTokenData: (token: AuthTokenData) => void;
  setUserInfo: (userInfo: AuthState["userInfo"]) => void;
  setRegisteringUser: (registeringUser: AuthState["registeringUser"]) => void;
  clearRegisteringUser: () => void;
  logout: () => void;
  isLoggedIn: () => boolean;
}

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      userInfo: null,
      registeringUser: null,
      accessToken: null,
      refreshToken: null,
      expiresIn: null,
      tokenType: null,

      setTokenData: (token) => set((state) => ({ ...state, ...token })),
      setUserInfo: (userInfo) => set((state) => ({ ...state, userInfo })),
      setRegisteringUser: (registeringUser) => set((state) => ({ ...state, registeringUser })),
      clearRegisteringUser: () => set((state) => ({ ...state, registeringUser: null })),
      logout: () => {
        // Clear auth state
        set((state) => ({
          ...state,
          accessToken: null,
          refreshToken: null,
          expiresIn: null,
          tokenType: null,
        }));
        
        // Only clear active jobId (not all metadata)
        // Metadata will be matched with API response when user logs in again
        // This allows user to continue from same progress if they log in with same account
        try {
          const JOB_ID_STORAGE_KEY = "course-generation-job-id";
          const TEMP_ACTIVE_JOB_ID_KEY = "temp-active-job-id";
          
          // Remove only active jobId entries (not metadata)
          // Metadata will be preserved and matched with API response on next login
          localStorage.removeItem(JOB_ID_STORAGE_KEY);
          localStorage.removeItem(TEMP_ACTIVE_JOB_ID_KEY);
        } catch (error) {
          // Silently fail - don't block logout if localStorage operations fail
          console.warn("Failed to clear active jobId on logout:", error);
        }
      },
      isLoggedIn: () => !!get().accessToken,
    }),
    {
      name: "auth",
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const getAuthState = useAuthStore.getState;
